import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    ClientIntegrationSource,
    Employee,
    EmployeeIdentifier,
} from '../../../../../shared/interfaces/client-data';
import { EmployeeService } from '../../../../../shared/services/employee.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../../../../shared/services/toast.service';
import { ReusableFunctions, USAState } from '../../../../../shared/classes/reusable-functions';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { HierarchyEntity } from '../../../../interfaces/hierarchy-data';
import { IntegrationSourceService } from '../../../../services/integration-source.service';
import {
    EmployeePositionService,
    IClientStaffPositionDdl,
} from '../../../../services/employee-position.service';
import { ClientDomainService } from '../../../../services/client-domain.service';
import { ClientDomainModel } from '../../../../interfaces/client-domain-data';
import { CurrentUserStateService } from '../../../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../../../shared/services/impersonation.service';
import { EmployeeRecordViewRequirementsValidators } from '../../../../../shared/Validators/employee-record-view.validator';
import { ClientIntegrationService } from 'app/modules/settings/services/client-integration.service';
import { ClientIntegration } from 'app/modules/settings/interfaces/client-integration';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'app/modules/shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-employee-record-view',
    templateUrl: './employee-record-view.component.html',
    styleUrls: ['./employee-record-view.component.scss'],
})
export class EmployeeRecordViewComponent implements OnInit, OnChanges {
    @Input() selectedEmployee: Employee;
    @Input() editing: boolean;
    @Input() hierarchies: HierarchyEntity[];
    @Input() addEditOrViewOnly: 'add' | 'edit' | 'viewOnly' | 'nonPowerUserEdit';
    @Input() supervisors: Employee[];
    @Output() cancelClickedEvent = new EventEmitter();
    @Output() employeeModEvent = new EventEmitter();

    @HostListener('window:popstate', ['$event'])
    onPopState(event) {
        this.cancelClicked();
    }

    employeeDetailsExpanded: boolean;
    identityDetailsExpanded: boolean;
    userDetailsExpanded: boolean;
    isLBAUser: boolean;
    showProfilePicUploadDialog: boolean;
    profilePicSrc: string;
    userProfileView: boolean;
    fileMaxSizeInMB: number;
    menuClicked: boolean;
    employeeForm: UntypedFormGroup;
    hierarchyListSettings: any;
    states: USAState[];
    employeeIdentifiers: EmployeeIdentifier[];
    clientIntegrationSources: ClientIntegrationSource[];
    checkingIDForUniqueness: boolean;
    isIdentifierUnique: boolean;
    checkingEmailForUniqueness: boolean;
    updateOn: 'change' | 'blur' | 'submit';
    loading: boolean = false;
    clientStaffPositions: IClientStaffPositionDdl[];
    error: any;
    submitting: boolean;
    showAddJobTitle: boolean;
    supervisorLFName: string;
    approvedDomains: ClientDomainModel[];
    primaryIdentifier: string;
    zeroPaddingFlag: boolean = false;
    zeroPaddingLength: number;

    constructor(
        private _employeeService: EmployeeService,
        private _employeePositionService: EmployeePositionService,
        private _activatedRoute: ActivatedRoute,
        public dialog: MatDialog,
        private _toastr: ToastService,
        private _reusableFunctions: ReusableFunctions,
        private _formBuilder: UntypedFormBuilder,
        private _clientIntegrationSourceService: IntegrationSourceService,
        private _clientIntegrationService: ClientIntegrationService,
        private _domainService: ClientDomainService,
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService,
        private router: Router
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.addEditOrViewOnly) {
            if (changes.selectedEmployee && changes.selectedEmployee.currentValue) {
                if (this.supervisors) {
                    let supervisor = this.supervisors.find(
                        (e) => e.primaryIdentifier === this.selectedEmployee.supervisorIdentifier
                    );
                    if (supervisor) {
                        this.supervisorLFName = supervisor.lfName;
                    }
                    this.supervisors = this.supervisors.filter(
                        (e) => e.primaryIdentifier !== this.selectedEmployee.primaryIdentifier
                    );
                }
                this._employeeService
                    .getAllEmployeeIdentityRecords(this.selectedEmployee.primaryIdentifier)
                    .subscribe(
                        (identifiers) => {
                            this.employeeIdentifiers = identifiers;
                            if (this.addEditOrViewOnly !== 'add') {
                                this.addEmployeeIdentifiersFormControls();
                            }
                        },
                        (error) => {
                            throw new Error(error);
                        }
                    );
            }
        }
    }

    setSupervisorLFName() {
        if (this.supervisors) {
            let supervisor = this.supervisors.find(
                (e) => e.primaryIdentifier === this.supervisorIdentifier.value
            );
            if (supervisor) {
                this.supervisorLFName = supervisor.lfName;
            } else {
                this.supervisorLFName = null;
            }
        }
    }

    ngOnInit() {
        scroll(0, 0);
        if (this.addEditOrViewOnly === 'edit') {
            this.updateOn = 'change';
        } else {
            this.updateOn = 'blur';
        }

        this.checkingIDForUniqueness = false;
        this.checkingEmailForUniqueness = false;
        this.isIdentifierUnique = true;
        if (!this.addEditOrViewOnly) {
            this.addEditOrViewOnly = 'viewOnly';
        }
        this.states = [{ name: '', abbreviation: '' }].concat(
            this._reusableFunctions.getUSAStatesJson()
        );

        // Remove duplicate hierarchy entities
        // The reused sproc has the potential to return duplicates
        if (this.hierarchies != null && this.hierarchies.length > 0) {
            this.hierarchies = Array.from(new Set(this.hierarchies.map((h) => h.hierarchyKey))).map(
                (hierarchyKey) => {
                    let item = this.hierarchies.find((h) => h.hierarchyKey === hierarchyKey);
                    return {
                        hierarchyKey: hierarchyKey,
                        hierarchyManagementKey: item.hierarchyManagementKey,
                        hierarchyIdentifier: item.hierarchyIdentifier,
                        costCenterIdentifier: item.costCenterIdentifier,
                        nmlsIdentifier: item.nmlsIdentifier,
                        name: item.name,
                        nameAbbreviated: item.nameAbbreviated,
                        owner: item.owner,
                        ownerIdentifier: item.ownerIdentifier,
                        level: item.level,
                        levelOrder: item.levelOrder,
                        parentHierarchyKey: item.parentHierarchyKey,
                        parentName: item.parentName,
                        activeDate: item.activeDate,
                        inActiveDate: item.inActiveDate,
                        alternateEntityNumber: item.alternateEntityNumber,
                        simpleNexusIdentifier: item.simpleNexusIdentifier,
                    };
                }
            );
        }

        this.hierarchyListSettings = {
            singleSelection: true,
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'name',
            primaryKey: 'hierarchyIdentifier',
            position: top,
            escapeToClose: true,
        };
        this._domainService.getDomains().subscribe(
            (data) => {
                this.approvedDomains = data;
            },
            (error) => {
                throw new Error(error);
            }
        );
        this.employeeForm = this.createFormGroup();

        this.onChanges();
        this.isLBAUser = false;
        this.employeeDetailsExpanded = true;
        this.userDetailsExpanded = true;
        this.identityDetailsExpanded = true;
        this.fileMaxSizeInMB = 250;

        let currentUser = this.currentUserStateService.currentUser;
        let clientShortName = currentUser.clientShortName;

        if (
            (this._activatedRoute.snapshot.url.length === 0 ||
                this._activatedRoute.snapshot.url[0].path !== 'employees') &&
            clientShortName === 'LBA' &&
            !this.impersonationService.isImpersonatingUser()
        ) {
            if (!this.impersonationService.isImpersonatingClient() || !this.selectedEmployee) {
                this.isLBAUser = true;
                return;
            }
        }

        if (!this.selectedEmployee && this.addEditOrViewOnly === 'viewOnly') {
            this._employeeService.getCurrentUsersEmployeeRecord().subscribe((record) => {
                this.selectedEmployee = record;

                this._employeeService
                    .getAllEmployeeIdentityRecords(this.selectedEmployee.primaryIdentifier)
                    .subscribe(
                        (identifiers) => {
                            this.employeeIdentifiers = identifiers;
                            if (this.addEditOrViewOnly !== 'add') {
                                this.addEmployeeIdentifiersFormControls();
                            }
                        },
                        (error) => {
                            throw new Error(error);
                        }
                    );

                this.primaryIdentifier = this.getPrimaryEmployeeIdentifier();
                this.populateFormControls();
            });
        } else if (this.addEditOrViewOnly !== 'add') {
            this.populateFormControls();
        } else if (this.addEditOrViewOnly === 'add') {
            this._clientIntegrationSourceService
                .getClientIntegrationSources()
                .subscribe((sources) => {
                    this.clientIntegrationSources = sources;
                    this.addEmployeeIdentifiersFormControls();
                });
        }
        if (this.selectedEmployee) {
            this.selectedEmployee.mobilePhone = this.clearPhoneNumberIfInvalid(
                this.selectedEmployee.mobilePhone
            );

            if (this.hierarchies && this.hierarchies.length > 0) {
                let tmpHierarchy = this.hierarchies.find(
                    (h) => h.hierarchyIdentifier === this.locationIdentifier.value
                );

                if (tmpHierarchy && tmpHierarchy.name) {
                    this.selectedEmployee.location = tmpHierarchy.name;
                }
            }
        }
        this.userProfileView =
            (this._activatedRoute.snapshot.url.length === 0 ||
                (this._activatedRoute.snapshot.url.length > 0 &&
                    this._activatedRoute.snapshot.url[0].path !== 'employees')) &&
            !currentUser.impersonation;
        this.getClientStaffPositions();
        this.getZeroPadding();
    }

    getClientStaffPositions() {
        this._employeePositionService.getClientStaffPositionDDL().subscribe(
            (positions) => {
                this.clientStaffPositions = positions;
            },
            (error) => {
                this.error = error;
                throw new Error(error);
            }
        );
    }

    getZeroPadding() {
        this._clientIntegrationService
            .getClientIntegrationTypeByIntegrationType('Employees')
            .subscribe(
                (data) => {
                    let integration: ClientIntegration = data;
                    this.zeroPaddingFlag = integration.zeroPaddedIdentifier;
                    this.zeroPaddingLength = integration.zeroPaddedLen;
                },
                (error) => {
                    this.error = error;
                    throw new Error(error);
                }
            );
    }

    onChanges() {
        this.firstName.valueChanges.subscribe((val) => {
            if (this.addEditOrViewOnly === 'add') {
                this.preferredName.setValue(val);
            }
        });
        this.hireDate.valueChanges.subscribe((_) => {
            this.selectedEmployee.lengthOfEmployment = this.calculateLengthOfEmployment();
        });
        this.terminationDate.valueChanges.subscribe(() => {
            this.employeeStatus.setValue(this.calculateEmploymentStatus());
            this.selectedEmployee.lengthOfEmployment = this.calculateLengthOfEmployment();
            this.updateUserStatus();
        });
        this.hireDate.valueChanges.subscribe(() => {
            this.employeeStatus.setValue(this.calculateEmploymentStatus());
            this.selectedEmployee.lengthOfEmployment = this.calculateLengthOfEmployment();
        });
        this.onLeave.valueChanges.subscribe((_) => {
            this.employeeStatus.setValue(this.calculateEmploymentStatus());
        });
        this.supervisorIdentifier.valueChanges.subscribe((_) => {
            this.setSupervisorLFName();
        });
    }

    updateUserStatus() {
        if (this.employeeStatus.value == 'Terminated') {
            this.userStatus.setValue('Inactive');
            this.userStatus.disable();
        } else {
            this.userStatus.setValue(this.selectedEmployee.userStatus);
        }
    }

    calculateEmploymentStatus(): string {
        const millisecondsInDay = 86400000;
        let hireDate: number = +(this.hireDate.value ?? this.selectedEmployee.hireDate);
        let terminationDate: number = +(
            this.terminationDate.value ?? this.selectedEmployee.terminationDate
        );
        let epochDays: number = Math.floor(Date.now() / millisecondsInDay);
        if (!hireDate || hireDate === 0) {
            return this.selectedEmployee.employeeStatus;
        }
        if (!terminationDate || terminationDate === 0) {
            if (this.onLeave.value === 'true') {
                return 'On Leave';
            } else {
                return 'Active';
            }
        }
        let terminatedDays = Math.floor(terminationDate / millisecondsInDay);
        if (terminatedDays <= epochDays) {
            return 'Terminated';
        }

        if (this.onLeave.value === 'true') {
            return 'On Leave';
        }

        return 'Active';
    }

    populateFormControls() {
        if (this.selectedEmployee) {
            this.firstName.setValue(this.selectedEmployee.firstName);
            this.lastName.setValue(this.selectedEmployee.lastName);
            this.preferredName.setValue(this.selectedEmployee.firstNameProper);
            this.primaryEmail.setValue(this.selectedEmployee.loginEmail);
            this.origPrimaryIdentifier.setValue(this.selectedEmployee.primaryIdentifier);
            this.jobTitle.setValue(this.selectedEmployee.jobTitle);

            // Numbers were coming in as "-" for some employees
            if (this.selectedEmployee.mobilePhone && this.selectedEmployee.mobilePhone.length < 3) {
                this.selectedEmployee.mobilePhone = null;
            }

            this.mobilePhone.setValue(
                this.clearPhoneNumberIfInvalid(this.selectedEmployee.mobilePhone)
            );

            this.locationIdentifier.setValue(this.selectedEmployee.locationIdentifier);
            this.isSupervisor.setValue(
                this.selectedEmployee.isSupervisor == null
                    ? 'Unknown'
                    : this.selectedEmployee.isSupervisor
                    ? 'True'
                    : 'False'
            );
            this.supervisorIdentifier.setValue(this.selectedEmployee.supervisorIdentifier);
            this.city.setValue(this.selectedEmployee.city);
            this.state.setValue(this.selectedEmployee.state);
            this.onLeave.setValue(this.selectedEmployee.onLeave ? 'true' : 'false');
            this.hireDate.setValue(
                this._reusableFunctions.getDateFromDateTimeOffset(this.selectedEmployee.hireDate)
            );
            this.terminationDate.setValue(
                this._reusableFunctions.getDateFromDateTimeOffset(
                    this.selectedEmployee.terminationDate
                )
            );
            this.employmentType.setValue(
                this.selectedEmployee.fullTime ? 'Full-Time' : 'Part-Time'
            );
            this.isUser.setValue(this.selectedEmployee.userGUID ? true : false);
            this.updateUserStatus();
            this.userGUID.setValue(this.selectedEmployee.userGUID);
            this.employeeStatus.setValue(this.calculateEmploymentStatus());
            this.selectedEmployee.lengthOfEmployment = this.calculateLengthOfEmployment();
        } else {
            throw new Error('this.selectedEmployee is undefined.');
        }
    }
    clearPhoneNumberIfInvalid(phoneNumber: string) {
        if (phoneNumber && phoneNumber.length < 10) {
            return '';
        }
        return phoneNumber;
    }
    getClientStaffPositionKey(): number {
        if (!this.selectedEmployee) {
            throw new Error('Selected employee must be defined.');
        }
        if (!this.clientStaffPositions) {
            throw new Error('ClientStaffPositions are undefined.');
        }
        let clientStaffPosition = this.clientStaffPositions.find(
            (position) => position.clientPosition === this.selectedEmployee.jobTitle
        );
        if (clientStaffPosition) {
            return clientStaffPosition.clientStaffPositionKey;
        } else {
            throw new Error('ClientStaffPosition not found.');
        }
    }

    createFormGroup(): UntypedFormGroup {
        return this._formBuilder.group({
            contactInformation: this._formBuilder.group({
                firstName: new UntypedFormControl('', {
                    validators: [Validators.required, Validators.maxLength(25)],
                    updateOn: this.updateOn,
                }),
                lastName: new UntypedFormControl('', {
                    validators: [Validators.required, Validators.maxLength(25)],
                    updateOn: this.updateOn,
                }),
                preferredName: new UntypedFormControl('', {
                    validators: [Validators.required, Validators.maxLength(25)],
                    updateOn: this.updateOn,
                }),
                origPrimaryIdentifier: new UntypedFormControl(),
                primaryEmail: new UntypedFormControl('', {
                    validators: [Validators.required, Validators.email, Validators.maxLength(100)],
                    updateOn: 'blur',
                }),
                mobilePhone: new UntypedFormControl('', {
                    validators: [Validators.minLength(10), Validators.maxLength(10)],
                    updateOn: 'blur',
                }),
                jobTitle: new UntypedFormControl('', {
                    validators: [Validators.required, Validators.maxLength(100)],
                    updateOn: 'change',
                }),
                locationIdentifier: new UntypedFormControl('', {
                    validators: [Validators.required],
                    updateOn: 'change',
                }),
                supervisor: new UntypedFormControl(),
                isSupervisor: new UntypedFormControl('False'),
                supervisorIdentifier: new UntypedFormControl(''),
                city: new UntypedFormControl('', {
                    validators: [Validators.maxLength(75)],
                    updateOn: this.updateOn,
                }),
                state: new UntypedFormControl('', {
                    validators: [Validators.maxLength(2)],
                    updateOn: this.updateOn,
                }),
            }),
            employeeIdentifierDetails: this._formBuilder.array([], Validators.required),
            employeeDetails: this._formBuilder.group({
                employeeStatus: new UntypedFormControl('Active', {
                    validators: Validators.required,
                    updateOn: 'change',
                }),
                onLeave: new UntypedFormControl(false),
                hireDate: new UntypedFormControl('', {
                    validators: Validators.required,
                    updateOn: 'change',
                }),
                terminationDate: new UntypedFormControl(null, {
                    validators: [],
                    updateOn: 'change',
                }),
                employmentType: new UntypedFormControl('Full-Time', {
                    validators: Validators.required,
                    updateOn: 'change',
                }),
            }),
            userDetails: this._formBuilder.group({
                isUser: new UntypedFormControl(),
                userStatus: new UntypedFormControl(),
                userGUID: new UntypedFormControl(),
                userCreateDate: new UntypedFormControl(),
            }),
        });
    }

    addEmployeeIdentifiersFormControls() {
        if (this.addEditOrViewOnly === 'add') {
            this.clientIntegrationSources.forEach((integrationSource) => {
                const identityGroup: UntypedFormGroup = this._formBuilder.group({
                    identifier: new UntypedFormControl('', {
                        validators: integrationSource.isPrimaryForEmployeeIdentifier
                            ? [
                                  EmployeeRecordViewRequirementsValidators.IdentitiferRequired(
                                      integrationSource.isPrimary == null
                                          ? integrationSource.isPrimaryForEmployeeIdentifier
                                          : integrationSource.isPrimary
                                  ),
                                  Validators.maxLength(20),
                              ]
                            : [
                                  EmployeeRecordViewRequirementsValidators.ZeroPaddingRequired(
                                      this.zeroPaddingFlag,
                                      this.zeroPaddingLength
                                  ),
                                  Validators.maxLength(20),
                              ],
                        updateOn: 'change',
                    }),
                    identifierType: new UntypedFormControl(
                        integrationSource.identifierType,
                        Validators.required
                    ),
                    clientIntegrationSource: new UntypedFormControl(integrationSource.source),
                    description: new UntypedFormControl(
                        integrationSource.description,
                        Validators.required
                    ),
                    isPrimary: new UntypedFormControl(
                        integrationSource.isPrimaryForEmployeeIdentifier,
                        Validators.required
                    ),
                });

                (this.employeeForm.controls.employeeIdentifierDetails as UntypedFormArray).push(
                    identityGroup
                );
            });
        } else {
            this.employeeIdentifiers.forEach((identity) => {
                const identityGroup: UntypedFormGroup = this._formBuilder.group({
                    identifier: new UntypedFormControl(identity.identifier, {
                        validators: identity.isPrimary
                            ? [
                                  EmployeeRecordViewRequirementsValidators.IdentitiferRequired(
                                      identity.isPrimary
                                  ),
                                  EmployeeRecordViewRequirementsValidators.ZeroPaddingRequired(
                                      this.zeroPaddingFlag,
                                      this.zeroPaddingLength
                                  ),
                                  Validators.maxLength(20),
                              ]
                            : [
                                  EmployeeRecordViewRequirementsValidators.ZeroPaddingRequired(
                                      this.zeroPaddingFlag,
                                      this.zeroPaddingLength
                                  ),
                                  Validators.maxLength(20),
                              ],
                        updateOn: 'change',
                    }),
                    identifierType: new UntypedFormControl(
                        identity.identifierType,
                        Validators.required
                    ),
                    clientIntegrationSource: new UntypedFormControl(
                        identity.clientIntegrationSource
                    ),
                    description: new UntypedFormControl(identity.description, Validators.required),
                    isPrimary: new UntypedFormControl(identity.isPrimary, Validators.required),
                    employeeIdentifierKey: new UntypedFormControl(identity.employeeIdentifierKey),
                });

                identityGroup.get('identifier').markAsDirty();
                (this.employeeForm.controls.employeeIdentifierDetails as UntypedFormArray).push(
                    identityGroup
                );
            });
        }
    }

    get identities() {
        return (<UntypedFormArray>this.employeeForm.get('employeeIdentifierDetails')).controls;
    }

    get firstName() {
        return this.employeeForm.get('contactInformation').get('firstName');
    }

    get lastName() {
        return this.employeeForm.get('contactInformation').get('lastName');
    }

    get preferredName() {
        return this.employeeForm.get('contactInformation').get('preferredName');
    }

    get primaryEmail() {
        return this.employeeForm.get('contactInformation').get('primaryEmail');
    }

    get mobilePhone() {
        return this.employeeForm.get('contactInformation').get('mobilePhone');
    }

    get origPrimaryIdentifier() {
        return this.employeeForm.get('contactInformation').get('origPrimaryIdentifier');
    }
    n;
    get jobTitle() {
        return this.employeeForm.get('contactInformation').get('jobTitle');
    }

    get locationIdentifier() {
        return this.employeeForm.get('contactInformation').get('locationIdentifier');
    }

    get isSupervisor() {
        return this.employeeForm.get('contactInformation').get('isSupervisor');
    }

    get supervisorIdentifier() {
        return this.employeeForm.get('contactInformation').get('supervisorIdentifier');
    }

    get supervisor() {
        return this.employeeForm.get('contactInformation').get('supervisor');
    }

    get city() {
        return this.employeeForm.get('contactInformation').get('city');
    }

    get state() {
        return this.employeeForm.get('contactInformation').get('state');
    }

    get employeeStatus() {
        return this.employeeForm.get('employeeDetails').get('employeeStatus');
    }

    get hireDate() {
        return this.employeeForm.get('employeeDetails').get('hireDate');
    }

    get terminationDate() {
        return this.employeeForm.get('employeeDetails').get('terminationDate');
    }

    get onLeave() {
        return this.employeeForm.get('employeeDetails').get('onLeave');
    }

    get employmentType() {
        return this.employeeForm.get('employeeDetails').get('employmentType');
    }

    get isUser() {
        return this.employeeForm.get('userDetails').get('isUser');
    }

    get userStatus() {
        return this.employeeForm.get('userDetails').get('userStatus');
    }

    get userGUID() {
        return this.employeeForm.get('userDetails').get('userGUID');
    }

    calculateLengthOfEmployment(): string {
        const millisecondsInDay = 86400000;
        let hireDate: number = +(this.hireDate.value ?? this.selectedEmployee.hireDate);
        if (!hireDate || hireDate === 0) {
            return '';
        }
        let endDate = +new Date();
        if (this.terminationDate.value) {
            endDate = +new Date(this.terminationDate.value);
        }
        let duration = endDate - hireDate;
        let remainingDaysOfEmployment = Math.floor(duration / millisecondsInDay);
        let employmentYears = Math.floor(remainingDaysOfEmployment / 365);
        remainingDaysOfEmployment -= employmentYears * 365;
        let employmentMonths = Math.floor(remainingDaysOfEmployment / 30);

        return employmentYears > 0
            ? employmentYears.toString() + ' years, ' + employmentMonths.toString() + ' months'
            : employmentMonths.toString() + ' months';
    }

    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 27) {
            //keyCode 27 is the escape key.
            this.cancelClicked();
        }
    }

    mailto(email: string) {
        window.location.href = 'mailto: ' + email;
    }

    cancelClicked() {
        this.editing = false;
        this.cancelClickedEvent.emit();
        this.employeeForm.reset();
    }

    onSubmit() {
        this.submitting = true;
        this.employeeForm.markAsPristine();
        if (this.identifiersInThisFormMatch()) {
            this._toastr.warning('You have two matching Employee Numbers.');
            //I chose to handle this in a toast because the logic will
            //be changing to allow IDs to match as long as the ID is not
            //in use for the specific integration system.
            return;
        }
        let saveIsSupervisor = this.isSupervisor.value;
        this.isSupervisor.setValue(
            this.isSupervisor.value == 'Unknown'
                ? null
                : this.isSupervisor.value == 'True'
                ? true
                : false
        );
        let formatValue = this.formatRawFormValue();
        let hasValidDomain = this.isDomainValid();
        if (this.addEditOrViewOnly === 'add' && hasValidDomain) {
            this._employeeService.insertEmployee(formatValue).subscribe(
                () => {
                    this._toastr.success('Employee created.');
                    this.addEditOrViewOnly = 'viewOnly';
                    this.editing = false;
                    this.employeeModEvent.emit();
                    this.employeeForm.markAsPristine();
                    this.submitting = false;
                    this.updateSelectedEmployee();
                },
                (error) => {
                    this._toastr.warning('Employee create error.');
                    this.submitting = false;
                    throw new Error(error);
                }
            );
        } else if (this.addEditOrViewOnly !== 'add' && hasValidDomain) {
            this._employeeService.updateEmployeeRequest(formatValue).subscribe(
                () => {
                    this._toastr.success('Employee updated.');
                    this.editing = false;
                    this.addEditOrViewOnly = 'viewOnly';
                    let primaryIdentifer = this.getPrimaryEmployeeIdentifier();
                    this.origPrimaryIdentifier.setValue(primaryIdentifer);
                    this.employeeModEvent.emit(primaryIdentifer);
                    this.employeeForm.markAsPristine();
                    this.submitting = false;
                    this.updateSelectedEmployee();
                },
                (error) => {
                    this._toastr.warning('Employee update error.');
                    this.submitting = false;
                    throw new Error(error);
                }
            );
        } else {
            this._toastr.warning('Email domain not in Client list of acceptable domains');
        }

        this.isSupervisor.setValue(saveIsSupervisor);
    }
    updateSelectedEmployee() {
        this.selectedEmployee.mobilePhone = this.clearPhoneNumberIfInvalid(this.mobilePhone.value);
        this.selectedEmployee.firstName = this.firstName.value;
        this.selectedEmployee.lastName = this.lastName.value;
        this.selectedEmployee.firstNameProper = this.preferredName.value;

        let tmpHierarchy = this.hierarchies.find(
            (h) => h.hierarchyIdentifier === this.locationIdentifier.value
        );

        if (tmpHierarchy && tmpHierarchy.name) {
            this.selectedEmployee.location = tmpHierarchy.name;
        }

        this.selectedEmployee.loginEmail = this.primaryEmail.value;
        this.selectedEmployee.jobTitle = this.jobTitle.value;
        this.selectedEmployee.locationIdentifier = this.locationIdentifier.value;
        this.selectedEmployee.isSupervisor = this.isSupervisor.value;
        this.selectedEmployee.supervisorIdentifier = this.supervisorIdentifier.value;
        this.selectedEmployee.city = this.city.value;
        this.selectedEmployee.state = this.state.value;
        this.selectedEmployee.onLeave = this.onLeave.value;
        this.selectedEmployee.hireDate = this.hireDate.value;
        this.selectedEmployee.terminationDate = this.terminationDate.value;
        this.selectedEmployee.fullTime = this.employmentType.value === 'Full-Time';
        this.selectedEmployee.lengthOfEmployment = this.calculateLengthOfEmployment();
        this.selectedEmployee.userStatus = this.userStatus.value;
    }

    formatRawFormValue(): any {
        let formValues: any = this.employeeForm.getRawValue();
        try {
            formValues.contactInformation.clientStaffPositionKey = this.clientStaffPositions.find(
                (x) => x.clientPosition === formValues.contactInformation.jobTitle
            ).clientStaffPositionKey;
            if (formValues.contactInformation.supervisorIdentifier === 'N/A')
                formValues.contactInformation.supervisorIdentifier = null;
        } catch (ex) {
            throw new Error(ex);
        }
        formValues.employeeDetails.onLeave = formValues.employeeDetails.onLeave === 'true';
        return formValues;
    }

    identifiersInThisFormMatch() {
        let identityValues = this.identities
            .map((identity) => identity.get('identifier').value)
            .filter((id) => id);
        var matchExists = identityValues.some((id, index) => {
            return id.length > 0 && identityValues.indexOf(id) !== index;
        });
        return matchExists;
    }

    getPrimaryEmployeeIdentifier(): any {
        if (this.identities.length > 0) {
            return this.identities.find((identity) => identity.get('isPrimary')).value.identifier;
        }
        return null;
    }

    employeeIdentifierChangeHandler(index: number) {
        let integrationSource = this.identities[index].get('clientIntegrationSource').value;
        let integrationDescription = this.identities[index].get('description').value;
        let identifier = this.identities[index].get('identifier').value;
        if (!identifier) {
            return;
            //don't check empty string;
        }
        if (
            this.employeeIdentifiers &&
            this.employeeIdentifiers.length > index &&
            this.identities[index].get('identifier').value !==
                this.employeeIdentifiers[index].identifier
        ) {
            this.checkingIDForUniqueness = true;
            this.checkIdentifierForUniqueness(
                integrationSource,
                integrationDescription,
                identifier,
                index
            );
        } else if (this.addEditOrViewOnly === 'add') {
            this.checkingIDForUniqueness = true;
            this.checkIdentifierForUniqueness(
                integrationSource,
                integrationDescription,
                identifier,
                index
            );
        }

        if (this.zeroPaddingFlag && identifier.length < this.zeroPaddingLength) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                data: {
                    title: 'Zero Padding',
                    message: `Your identifier will be zero padded to satisfy the minimum length requirement of ${this.zeroPaddingLength} characters. Continue?`,
                },
            });

            dialogRef
                .afterClosed()
                .toPromise()
                .then((result) => {
                    this.zeroPadEmployeeIdentifier(result, identifier, index);
                });
        }
    }

    employeeIdentifierChangeWarning(index: number) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                message: `You are about to update an Employee Identifier. Please ensure that the new identifier corresponds with the applicable platform. Continue?`,
            },
        });
    }

    zeroPadEmployeeIdentifier(result: any, identifier: string, index: number) {
        if (
            result == 'accept' &&
            identifier?.length > 0 &&
            this.zeroPaddingFlag == true &&
            this.zeroPaddingLength != 0
        ) {
            let paddedIdentifier = identifier.padStart(this.zeroPaddingLength, '0');
            this.identities[index].get('identifier').setValue(paddedIdentifier);
        } else {
            this.identities[index].get('identifier').setValue(identifier);
        }
    }

    employeeEmailChangeHandler() {
        if (!this.primaryEmail.value) {
            return;
        }
        if (
            this.primaryEmail.dirty &&
            this.primaryEmail.value &&
            (!this.selectedEmployee.loginEmail ||
                this.selectedEmployee.loginEmail.toLowerCase() !==
                    this.primaryEmail.value.toLowerCase())
        ) {
            this.checkEmailForUniqueness(this.primaryEmail.value);
        }
        if (!this.isDomainValid()) {
            this.primaryEmail.setErrors({ noDomain: true });
        }
    }

    isDomainValid() {
        if (this.primaryEmail.value) {
            let domainName = this.primaryEmail.value.split('@')[1]; // Get domain out of email
            if (domainName) {
                return (
                    this.approvedDomains.findIndex(
                        (domain) => domain.domain.toLowerCase() == domainName.toLowerCase()
                    ) !== -1
                );
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    private checkEmailForUniqueness(email: any) {
        this.checkingEmailForUniqueness = true;
        this._employeeService.isEmailUnique(email, this.origPrimaryIdentifier.value).subscribe(
            (isUnique) => {
                if (!isUnique) {
                    this.primaryEmail.setErrors({ notUnique: true });
                } else {
                }
                this.checkingEmailForUniqueness = false;
            },
            (error) => {
                this.checkingEmailForUniqueness = false;
                throw new Error(error);
            }
        );
    }

    private checkIdentifierForUniqueness(
        integrationSource: any,
        integrationDescription: any,
        identifier: any,
        index: number
    ) {
        this._clientIntegrationSourceService
            .isIdentifierUniqueForSource(integrationSource, integrationDescription, identifier)
            .subscribe((isUnique) => {
                if (!isUnique) {
                    this.identities[index].get('identifier').setErrors({ notUnique: true });
                } else {
                    this.identities[index].get('identifier').setErrors(null);
                }
                this.checkingIDForUniqueness = false;
            });
    }

    newJobTitleHandler(jobTitle: string) {
        this.jobTitle.setValue(jobTitle);
        this.employeeForm.markAsDirty();
        this.showAddJobTitle = false;
        this.getClientStaffPositions();
    }

    // Used by [disabled] on save button for non power user employee edit
    canSubmitNonPowerUserEdit(): boolean {
        if (
            this.firstName.value == this.selectedEmployee.firstName &&
            this.lastName.value == this.selectedEmployee.lastName &&
            this.preferredName.value == this.selectedEmployee.firstNameProper &&
            this.mobilePhone.value == this.selectedEmployee.mobilePhone
        ) {
            return false; // no changes made
        }

        return (
            this.firstName.valid &&
            this.lastName.valid &&
            this.preferredName.valid &&
            this.mobilePhone.valid
        );
    }

    // Save employee w/ non power user fields:
    // FirstName, LastName, PreferredName, Phone
    submitNonPowerUserEdit() {
        if (this.canSubmitNonPowerUserEdit()) {
            this.submitting = true;

            let data = {
                firstName: this.firstName.value,
                lastName: this.lastName.value,
                preferredName: this.preferredName.value,
                mobilePhone: this.mobilePhone.value,
            };

            this._employeeService.updateEmployeeSimpleContact(data).subscribe(
                (res) => {
                    this.submitting = false;
                    this.editing = false;
                    this.addEditOrViewOnly = 'viewOnly';

                    this._toastr.success('Your employee record has been updated!');
                    this.reloadComponentOnEditAndSave();
                },
                (error) => {
                    this.submitting = false;
                    this.editing = false;
                    this.addEditOrViewOnly = 'viewOnly';
                    throw new Error(error);
                }
            );
        }
    }

    editSimpleContact() {
        this.editing = true;
        this.addEditOrViewOnly = 'nonPowerUserEdit';
    }

    // This is bad practice but I can't figure out this component.
    reloadComponentOnEditAndSave() {
        let currentUrl = this.router.url;
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';
        this.router.navigate([currentUrl]);
    }
}
